<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Customers</h1>
          <p class="lede">{{ customers.length }} customer{{ customers.length === 1 ? '' : 's' }} on record.</p>
        </div>
        <div class="head-actions">
          <input v-model="search" placeholder="Search by name or phone…" style="width:260px" @keyup.enter="load" />
          <button class="secondary" @click="load">Search</button>
          <button v-if="can('customers.create')" class="primary" @click="openNew">+ Add customer</button>
        </div>
      </div>

      <div class="card">
        <table v-if="customers.length">
          <thead>
            <tr>
              <th>Name</th><th>Phone</th><th>Email</th>
              <th class="r">Visits</th><th class="r">Total spent</th><th class="r">Points</th>
              <th>Last visit</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in customers" :key="c.id" @click="openDetail(c)" class="clickable">
              <td><strong>{{ c.name || '—' }}</strong></td>
              <td>{{ c.phone || '—' }}</td>
              <td class="muted">{{ c.email || '—' }}</td>
              <td class="r">{{ c.visit_count }}</td>
              <td class="r">KES {{ format(c.total_spent) }}</td>
              <td class="r muted">{{ c.loyalty_points }}</td>
              <td class="muted small">{{ c.last_visit_at || '—' }}</td>
              <td><button class="secondary small" @click.stop="openEdit(c)">Edit</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No customers yet. Customers get added automatically when phone numbers are attached to sales at the till.</p>
      </div>
    </div>

    <!-- Add / Edit modal -->
    <div v-if="showForm" class="modal-back" @click.self="showForm=false">
      <div class="modal card">
        <h3>{{ form.id ? 'Edit Customer' : 'New Customer' }}</h3>
        <label>Name</label><input v-model="form.name" />
        <label>Phone</label><input v-model="form.phone" placeholder="0712345678" />
        <label>Email (optional)</label><input v-model="form.email" type="email" />
        <div class="modal-actions">
          <button class="secondary" @click="showForm=false">Cancel</button>
          <button class="primary" @click="save" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- Adjust points modal -->
    <div v-if="adjust" class="modal-back" @click.self="adjust=null">
      <div class="modal card">
        <h3>{{ adjust.sign > 0 ? 'Grant' : 'Deduct' }} loyalty points</h3>
        <p class="muted small">{{ detail.customer.name || 'Customer' }} · current balance: {{ detail.customer.loyalty_points }}</p>
        <label>{{ adjust.sign > 0 ? 'Points to grant' : 'Points to deduct' }}</label>
        <input type="number" v-model.number="adjust.points" min="1" />
        <label>Reason (required)</label>
        <input v-model="adjust.reason" placeholder="e.g. Loyalty bonus / correction" />
        <div class="modal-actions">
          <button class="secondary" @click="adjust=null">Cancel</button>
          <button :class="adjust.sign > 0 ? 'primary' : 'danger'" @click="submitAdjust" :disabled="!adjust.points || !adjust.reason || submittingAdjust">
            {{ submittingAdjust ? 'Saving…' : (adjust.sign > 0 ? 'Grant ' + adjust.points : 'Deduct ' + adjust.points) }}
          </button>
        </div>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card wide">
        <div class="detail-header">
          <div>
            <h3>
              {{ detail.customer.name || 'Unnamed' }}
              <span v-if="loyExtra && loyExtra.customer.tier_name"
                    class="tier-badge"
                    :style="{ background: loyExtra.customer.tier_color, color: '#fff' }">
                {{ loyExtra.customer.tier_name }}
              </span>
            </h3>
            <p class="muted">{{ detail.customer.phone || 'No phone' }} · {{ detail.customer.email || 'No email' }}</p>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>

        <!-- Tier progress -->
        <div v-if="loyExtra && loyExtra.nextTier" class="tier-progress">
          <div class="tier-progress-row">
            <span>
              <strong>KES {{ format(loyExtra.customer.loyalty_spend_ytd) }}</strong> spent this year
            </span>
            <span class="muted small">
              KES {{ format(Math.max(0, Number(loyExtra.nextTier.min_spend_ytd) - Number(loyExtra.customer.loyalty_spend_ytd))) }}
              to reach <strong :style="{ color: loyExtra.nextTier.color }">{{ loyExtra.nextTier.name }}</strong>
            </span>
          </div>
          <div class="tier-bar">
            <div class="tier-bar-fill" :style="{
              width: tierProgressPct + '%',
              background: loyExtra.nextTier.color,
            }"></div>
          </div>
        </div>
        <div v-else-if="loyExtra && loyExtra.customer.tier_name" class="tier-progress muted small">
          You're at the top tier. Perks: {{ loyExtra.customer.perks_note || '—' }}
        </div>
        <div v-if="loyExtra && (loyExtra.expiring.days_30 || loyExtra.expiring.days_60 || loyExtra.expiring.days_90)"
             class="expiring-panel">
          <i class="mdi mdi-clock-alert-outline"></i>
          Expiring soon:
          <strong v-if="loyExtra.expiring.days_30">{{ loyExtra.expiring.days_30 }} pts in 30 days</strong>
          <span v-if="loyExtra.expiring.days_60"> · {{ loyExtra.expiring.days_60 }} pts in 60</span>
          <span v-if="loyExtra.expiring.days_90"> · {{ loyExtra.expiring.days_90 }} pts in 90</span>
        </div>
        <div class="detail-kpis">
          <div class="kpi"><div class="kpi-label">Visits</div><div class="kpi-val">{{ detail.customer.visit_count }}</div></div>
          <div class="kpi"><div class="kpi-label">Total spent</div><div class="kpi-val">KES {{ format(detail.customer.total_spent) }}</div></div>
          <div class="kpi"><div class="kpi-label">Wallet balance</div><div class="kpi-val">KES {{ format(detail.customer.wallet_balance) }}</div></div>
          <div class="kpi teal"><div class="kpi-label">Loyalty points</div><div class="kpi-val">{{ detail.customer.loyalty_points }}</div></div>
          <div class="kpi"><div class="kpi-label">Last visit</div><div class="kpi-val small">{{ detail.customer.last_visit_at || 'Never' }}</div></div>
        </div>
        <div class="loyalty-actions">
          <button class="secondary small" @click="openAdjust(1)"><i class="mdi mdi-plus-circle-outline"></i> Grant points</button>
          <button class="secondary small" @click="openAdjust(-1)"><i class="mdi mdi-minus-circle-outline"></i> Deduct points</button>
        </div>

        <h4>Purchase history</h4>
        <table v-if="detail.sales.length">
          <thead><tr><th>Receipt</th><th>Status</th><th class="r">Total</th><th>When</th></tr></thead>
          <tbody>
            <tr v-for="s in detail.sales" :key="s.id">
              <td><code>{{ s.receipt_no }}</code></td>
              <td>{{ s.status }}</td>
              <td class="r">KES {{ format(s.total) }}</td>
              <td class="muted small">{{ s.created_at }}</td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center small">No purchases yet.</p>

        <h4 v-if="detail.loyalty.length" style="margin-top: 20px;">Loyalty ledger</h4>
        <table v-if="detail.loyalty.length">
          <thead><tr><th>When</th><th>Direction</th><th class="r">Points</th><th class="r">Balance after</th><th>Reason</th></tr></thead>
          <tbody>
            <tr v-for="l in detail.loyalty" :key="l.id">
              <td class="muted small">{{ l.created_at }}</td>
              <td>{{ l.direction }}</td>
              <td class="r">{{ l.direction === 'redeem' || l.direction === 'expire' ? '-' : '+' }}{{ l.points }}</td>
              <td class="r">{{ l.balance_after }}</td>
              <td class="muted">{{ l.reason || '—' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute, can } from '@/api';

export default {
  name: 'CustomerManagement',
  components: { Navigation },
  data() {
    return {
      customers: [], search: '',
      showForm: false, saving: false, err: '',
      form: { id: 0, name: '', phone: '', email: '' },
      detail: null,
      adjust: null, submittingAdjust: false,
    };
  },
  async mounted() { await this.load(); },
  computed: {
    loyExtra() { return this.detail && this.detail.loyaltyExtra ? this.detail.loyaltyExtra : null; },
    tierProgressPct() {
      if (!this.loyExtra || !this.loyExtra.nextTier) return 0;
      const spend = Number(this.loyExtra.customer.loyalty_spend_ytd) || 0;
      const target = Number(this.loyExtra.nextTier.min_spend_ytd) || 0;
      const floor = Number(this.loyExtra.customer.tier_min) || 0;
      if (target <= floor) return 100;
      const pct = ((spend - floor) / (target - floor)) * 100;
      return Math.max(0, Math.min(100, Math.round(pct)));
    },
  },
  methods: {
    can(k) { return can(k); },
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    async load() {
      const { data } = await execute({ function: 'listCustomers', search: this.search }, paysol);
      if (data.success) this.customers = data.data;
    },
    openNew() { this.form = { id: 0, name: '', phone: '', email: '' }; this.err = ''; this.showForm = true; },
    openEdit(c) { this.form = { id: c.id, name: c.name || '', phone: c.phone || '', email: c.email || '' }; this.err = ''; this.showForm = true; },
    async save() {
      this.saving = true; this.err = '';
      try {
        const { data } = await execute({ function: 'saveCustomer', ...this.form }, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showForm = false;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Save failed.'; }
      finally { this.saving = false; }
    },
    async openDetail(c) {
      const [{ data }, { data: loyData }] = await Promise.all([
        execute({ function: 'getCustomerDetail', id: c.id }, paysol),
        execute({ function: 'getCustomerLoyalty', customerId: c.id }, paysol),
      ]);
      if (data.success) {
        this.detail = data.data;
        if (loyData.success) this.detail.loyaltyExtra = loyData.data;
      }
    },
    openAdjust(sign) {
      this.adjust = { sign, points: 10, reason: '' };
    },
    async submitAdjust() {
      this.submittingAdjust = true;
      try {
        const delta = this.adjust.sign * Math.abs(this.adjust.points);
        const { data } = await execute({ function: 'adjustLoyaltyPoints', customerId: this.detail.customer.id, delta, reason: this.adjust.reason }, paysol);
        if (!data.success) { alert(data.message); return; }
        this.detail.customer.loyalty_points = data.data.balance;
        this.adjust = null;
        // refresh ledger
        const { data: fresh } = await execute({ function: 'getCustomerDetail', id: this.detail.customer.id }, paysol);
        if (fresh.success) this.detail = fresh.data;
      } finally { this.submittingAdjust = false; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1200px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; }
.head-actions { display: flex; gap: 8px; align-items: center; }
.clickable { cursor: pointer; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.center { text-align: center; padding: 40px 0; }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 780px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
button.small { padding: 4px 10px; font-size: 11px; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
.detail-header { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
.detail-header h3 { margin: 0 0 4px; }
.detail-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 8px; margin-bottom: 20px; }
.kpi { padding: 12px; background: var(--paysol-bg-elev); border-radius: 6px; border-left: 3px solid var(--paysol-teal); }
.kpi-label { color: var(--paysol-muted); font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-val { color: var(--paysol-text); font-size: 16px; font-weight: 700; margin-top: 3px; }
.kpi.teal { border-left-color: var(--paysol-teal); }
.loyalty-actions { display: flex; gap: 6px; margin: 10px 0 16px; }
.loyalty-actions button { display: inline-flex; align-items: center; gap: 4px; }
.tier-badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; letter-spacing: 0.5px; margin-left: 8px; vertical-align: middle; }
.tier-progress { padding: 10px 12px; background: var(--paysol-bg-elev); border-radius: 6px; margin: 0 0 14px; }
.tier-progress-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; font-size: 12px; }
.tier-bar { background: var(--paysol-line); height: 8px; border-radius: 4px; overflow: hidden; }
.tier-bar-fill { height: 100%; transition: width .3s; }
.expiring-panel { padding: 8px 12px; background: rgba(255,152,0,0.12); color: #b07600; border-left: 3px solid #ff9800; border-radius: 4px; font-size: 12px; margin-bottom: 14px; }
.expiring-panel i { margin-right: 4px; }
.loyalty-actions button i { font-size: 14px; }
h4 { color: var(--paysol-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 8px; }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
</style>
